import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Warehouse, MapPin, PackagePlus, ClipboardList, Users, ScanLine, FileSpreadsheet, RefreshCw, Boxes, ClipboardCheck, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import * as api from '@/services/seasonalWmsApi';
import { hasOperationAccess } from '@/utils/operationAccess';

const TILES = [
  { id: 'location-setup', op: 'seasonal-bundle-location-setup', title: 'Location Setup', desc: 'Define Halls → Floors → Rows → Positions (bins)', icon: MapPin, route: '/wms/seasonal-bundle/location-setup', color: 'bg-sky-600' },
  { id: 'put-away', op: 'seasonal-bundle-put-away', title: 'Packed Box Put Away', desc: 'Scan a box — system suggests a bin (one customer per bin)', icon: PackagePlus, route: '/wms/seasonal-bundle/put-away', color: 'bg-emerald-600' },
  { id: 'create-pick-slip', op: 'seasonal-bundle-create-pick-slip', title: 'Create Pick Slip', desc: 'Customer → Slug → boxes → create outward pick slip', icon: ClipboardList, route: '/wms/seasonal-bundle/create-pick-slip', color: 'bg-indigo-600' },
  { id: 'pick-slips', op: 'seasonal-bundle-pick-slips', title: 'Box Pick Slips', desc: 'List box pick slips & assign / view the picker', icon: ClipboardCheck, route: '/wms/seasonal-bundle/pick-slips', color: 'bg-violet-600' },
  { id: 'available-pickers', op: 'seasonal-bundle-available-pickers', title: 'Available Pickers', desc: 'Set today\u2019s Seasonal picker pool (auto-assign)', icon: Users, route: '/wms/seasonal-bundle/available-pickers', color: 'bg-teal-600' },
  { id: 'picking', op: 'seasonal-bundle-picking', title: 'Packed Bundle Picking', desc: 'Mobile — scan each box to pick an assigned slip', icon: ScanLine, route: '/wms/seasonal-bundle/picking', color: 'bg-amber-600' },
  { id: 'stock-report', op: 'seasonal-bundle-stock-report', title: 'Location Stock Report', desc: 'Bin-wise stock — cancel or relocate stored bundles', icon: Boxes, route: '/wms/seasonal-bundle/stock-report', color: 'bg-fuchsia-600' },
  { id: 'master-carton-history', op: 'seasonal-bundle-master-carton-history', title: 'Master Carton History', desc: 'Reprint packed master-carton slips (customer, QR & contents)', icon: Printer, route: '/wms/seasonal-bundle/master-carton-history', color: 'bg-rose-600' },
  { id: 'packing-report', op: 'seasonal-packing-report', title: 'Seasonal Packing Report', desc: 'Per-box, per-barcode packed report', icon: FileSpreadsheet, route: '/wms/seasonal/packing-report', color: 'bg-lime-600' },
];

const StatChip = ({ label, value, testid }) => (
  <div className="bg-white/10 rounded-lg px-3 py-2 text-center" data-testid={testid}>
    <p className="text-2xl font-bold leading-none">{value}</p>
    <p className="text-[11px] text-[#E8DCC8] mt-1">{label}</p>
  </div>
);

const SeasonalBundleDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try { setStats(await api.getStats()); } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="seasonal-bundle-dashboard">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center">
            <Button variant="ghost" onClick={() => navigate('/wms/fg')} className="text-[#F9FAFB] hover:bg-white/10 mr-3" data-testid="sb-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Warehouse className="w-7 h-7 mr-3 text-lime-400" />
            <div className="flex-1">
              <h1 className="text-xl font-bold font-display">Seasonal Bundle WMS</h1>
              <p className="text-sm text-[#E8DCC8]">Store, pick-slip &amp; pick the seasonally packed boxes</p>
            </div>
            <Button variant="ghost" onClick={load} className="text-[#F9FAFB] hover:bg-white/10" data-testid="sb-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          {stats && (
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-4">
              <StatChip label="Pending Put-Away" value={stats.pending_putaway} testid="sb-stat-pending" />
              <StatChip label="Stored Boxes" value={stats.stored_boxes} testid="sb-stat-stored" />
              <StatChip label="Reserved" value={stats.reserved_boxes} testid="sb-stat-reserved" />
              <StatChip label="Open Slips" value={stats.open_pick_slips} testid="sb-stat-slips" />
              <StatChip label="Bins" value={stats.positions} testid="sb-stat-bins" />
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TILES.filter((t) => hasOperationAccess('fg_wms', t.op)).map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => navigate(t.route)}
                className="group text-left bg-white rounded-xl border border-[#E5DFD0] p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                data-testid={`sb-tile-${t.id}`}
              >
                <div className={`w-12 h-12 rounded-lg ${t.color} text-white flex items-center justify-center mb-3 group-hover:scale-105 transition-transform`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-base font-semibold text-[#1A1F2E]">{t.title}</h3>
                <p className="text-sm text-[#5A5F6E] mt-1">{t.desc}</p>
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default SeasonalBundleDashboard;
